import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { BellRing, Check, Loader2 } from 'lucide-react';
import { ALERT_OPS_DELIVERY_CHOICES, type AlertOpsDelivery, type AlertSettings, type AlertThresholds } from '@evi/shared';
import { getDisplayErrorMessage } from '@/lib/api';
import { useRecentRoute } from '@/hooks/useRecentRoute';
import {
  addressList, fetchAlertSettings, previewAlertSettings, previewRows, saveAlertSettings, type AlertPreview,
} from '@/lib/alerts';
import styles from './SettingsPage.module.css';

/**
 * Settings, Alerts (Warren, 29 Oct 2026, item 5: "per-type on/off, thresholds, digest time, EVI ops recipients, a 7-day
 * preview"; ruling G). Super Admin only: the nav shows the leaf to Super Admin, and the Api (routes/settings/alerts.ts)
 * answers 403 to anyone else, when the page shows nothing but that. Every save is audited with before and after.
 *
 * The environment stays the ceiling: a type it switches off stays off whatever is saved here, and the page says which.
 * The preview replays the last 7 days under the saved settings and under the edits, before Save.
 */
const KEY = ['alert-settings'];
const OPS_DELIVERY_LABELS: Record<AlertOpsDelivery, string> = { weekly_digest: 'Weekly overview on Monday (default)', standard: 'Standard (the same live triggers, and one morning email)', right_away: 'Right away' };

export function AlertSettingsPage() {
  useRecentRoute('Alerts');
  const client = useQueryClient();
  const q = useQuery({ queryKey: KEY, queryFn: fetchAlertSettings, retry: false, staleTime: 30_000 });
  const [draft, setDraft] = useState<AlertSettings | null>(null);
  const [opsText, setOpsText] = useState<string | null>(null);
  const [allowText, setAllowText] = useState<string | null>(null);
  const [saving, setSaving] = useState(false), [error, setError] = useState(''), [saved, setSaved] = useState(false);
  const [preview, setPreview] = useState<AlertPreview | null>(null), [previewing, setPreviewing] = useState(false), [previewError, setPreviewError] = useState('');

  if (q.isError) {
    return <section className={styles.platformFeeSection}><p className={styles.platformFeeHint} role="alert">Alert settings are Super Admin only.</p></section>;
  }
  const view = q.data;
  const base = draft ?? view?.value ?? null;
  // The address boxes edit as text; the value they make is what is saved and previewed.
  const value: AlertSettings | null = base && {
    ...base,
    ops: { ...base.ops, recipients: opsText === null ? base.ops.recipients : addressList(opsText) },
    staging_allowlist: allowText === null ? base.staging_allowlist : addressList(allowText),
  };
  const dirty = !!value && JSON.stringify(value) !== JSON.stringify(view?.value);
  const edit = (next: AlertSettings) => { setDraft(next); setSaved(false); };
  const setThreshold = (k: keyof AlertThresholds, raw: string) => {
    if (!value) return;
    const n = Number(raw);
    edit({ ...value, thresholds: { ...value.thresholds, [k]: Number.isFinite(n) ? n : value.thresholds[k] } });
  };

  async function save() {
    if (!value) return;
    setSaving(true); setError(''); setSaved(false);
    try {
      await saveAlertSettings(value);
      await client.invalidateQueries({ queryKey: KEY });
      setDraft(null); setOpsText(null); setAllowText(null); setSaved(true);
    } catch (err) { setError(await getDisplayErrorMessage(err, 'The settings were not saved.')); } finally { setSaving(false); }
  }
  async function runPreview() {
    setPreviewing(true); setPreviewError('');
    try { setPreview(await previewAlertSettings(dirty ? value : null)); }
    catch (err) { setPreviewError(await getDisplayErrorMessage(err, 'The preview did not run.')); } finally { setPreviewing(false); }
  }

  return <section className={styles.platformFeeSection}>
    <div className={styles.platformFeeHeader}>
      <div className={styles.headerIcon}><BellRing size={16} /></div>
      <div>
        <h2 className={styles.platformFeeTitle}>Alerts</h2>
        <p className={styles.platformFeeHint}>Weekly by default. Each site receives a Monday overview of the previous seven days. Choose a different frequency for a contact on the location page.</p>
      </div>
    </div>
    {q.isLoading || !value || !view ? <p className={styles.platformFeeHint}><Loader2 size={13} className={styles.spin} /> Loading.</p> : <>
      <p className={styles.platformFeeHint} role="status">
        The environment allows: offline alert email {view.effective.infra.offline_email ? 'on' : 'off'}, refused start email {view.effective.infra.refused_starts_email ? 'on' : 'off'},
        mail delivery {view.effective.infra.delivery_policy_open ? 'open' : 'held'}. A type it switches off stays off whatever is saved here.
      </p>

      <h3 className={styles.platformFeeTitle} style={{ marginTop: 12, fontSize: 15 }}>Which emails go</h3>
      <fieldset style={{ border: 'none', margin: '0 0 12px', padding: 0, display: 'flex', flexDirection: 'column', gap: 6 }} disabled={saving}>
        {view.type_keys.map((k) => <label key={k} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', minHeight: 28 }}>
          <input type="checkbox" checked={value.types[k]} onChange={(e) => edit({ ...value, types: { ...value.types, [k]: e.target.checked } })} style={{ marginTop: 3 }} />
          <span><strong>{view.labels[k]}</strong><br /><span className={styles.platformFeeHint}>{view.help[k]}</span></span>
        </label>)}
      </fieldset>

      <h3 className={styles.platformFeeTitle} style={{ fontSize: 15 }}>Thresholds</h3>
      <div className={styles.platformFeeField}>
        {(Object.keys(view.bounds.thresholds) as Array<keyof AlertThresholds>).map((k) => {
          const b = view.bounds.thresholds[k];
          return <div key={k} className={styles.platformFeeInputRow} style={{ alignItems: 'center', marginBottom: 6 }}>
            <label className={styles.platformFeeLabel} htmlFor={`alert-${k}`} style={{ flex: '1 1 260px', margin: 0 }}>{b.label} ({b.min} to {b.max})</label>
            <input id={`alert-${k}`} className={styles.platformFeeInput} style={{ maxWidth: 100 }} type="number" min={b.min} max={b.max} step={b.step}
              value={value.thresholds[k]} onChange={(e) => setThreshold(k, e.target.value)} disabled={saving} />
          </div>;
        })}
      </div>

      <h3 className={styles.platformFeeTitle} style={{ fontSize: 15 }}>Digests</h3>
      <div className={styles.platformFeeField}>
        <div className={styles.platformFeeInputRow} style={{ alignItems: 'center', marginBottom: 6 }}>
          <label className={styles.platformFeeLabel} htmlFor="alert-site-time" style={{ flex: '1 1 260px', margin: 0 }}>Morning summary, in each site&apos;s own time</label>
          <input id="alert-site-time" className={styles.platformFeeInput} style={{ maxWidth: 120 }} type="time" value={value.digest.site_time}
            onChange={(e) => edit({ ...value, digest: { ...value.digest, site_time: e.target.value } })} disabled={saving} />
        </div>
        <div className={styles.platformFeeInputRow} style={{ alignItems: 'center', marginBottom: 6 }}>
          <label className={styles.platformFeeLabel} htmlFor="alert-ops-time" style={{ flex: '1 1 260px', margin: 0 }}>EVI ops morning email</label>
          <input id="alert-ops-time" className={styles.platformFeeInput} style={{ maxWidth: 120 }} type="time" value={value.digest.ops_time}
            onChange={(e) => edit({ ...value, digest: { ...value.digest, ops_time: e.target.value } })} disabled={saving} />
        </div>
        <div className={styles.platformFeeInputRow} style={{ alignItems: 'center', marginBottom: 6 }}>
          <label className={styles.platformFeeLabel} htmlFor="alert-ops-zone" style={{ flex: '1 1 260px', margin: 0 }}>EVI ops time zone</label>
          <input id="alert-ops-zone" className={styles.platformFeeInput} style={{ maxWidth: 220 }} type="text" value={value.digest.ops_time_zone}
            onChange={(e) => edit({ ...value, digest: { ...value.digest, ops_time_zone: e.target.value.trim() } })} disabled={saving} />
        </div>
        <div className={styles.platformFeeInputRow} style={{ alignItems: 'center', marginBottom: 6 }}>
          <label className={styles.platformFeeLabel} htmlFor="alert-fallback-zone" style={{ flex: '1 1 260px', margin: 0 }}>A site with no time zone, and a tenant with none, uses</label>
          <input id="alert-fallback-zone" className={styles.platformFeeInput} style={{ maxWidth: 220 }} type="text" value={value.digest.fallback_time_zone}
            onChange={(e) => edit({ ...value, digest: { ...value.digest, fallback_time_zone: e.target.value.trim() } })} disabled={saving} />
        </div>
        <label style={{ display: 'flex', gap: 8, alignItems: 'center', margin: '4px 0 12px' }}>
          <input type="checkbox" checked={value.digest.skip_empty} onChange={(e) => edit({ ...value, digest: { ...value.digest, skip_empty: e.target.checked } })} disabled={saving} />
          Skip the morning summary on a day with nothing to report
        </label>
      </div>

      <h3 className={styles.platformFeeTitle} style={{ fontSize: 15 }}>EVI ops</h3>
      <label className={styles.platformFeeLabel} htmlFor="alert-ops-list">Recipients, @evinitiative.com only (at most {view.bounds.ops_recipients_max}). Empty: the address infra sets.</label>
      <textarea id="alert-ops-list" className={styles.platformFeeInput} rows={3} style={{ width: '100%', maxWidth: 420 }} disabled={saving}
        value={opsText ?? value.ops.recipients.join('\n')} onChange={(e) => { setOpsText(e.target.value); setSaved(false); }} />
      <p className={styles.platformFeeHint}>Emailed now: {view.effective.ops_recipients.length ? view.effective.ops_recipients.join(', ') : 'nobody'}
        {view.effective.ops_recipients_source === 'env' ? ' (from infra)' : ' (saved here)'}.</p>
      <label className={styles.platformFeeLabel} htmlFor="alert-ops-delivery">How EVI ops hears</label>
      <select id="alert-ops-delivery" className={styles.platformFeeInput} style={{ maxWidth: 420 }} value={value.ops.delivery} disabled={saving}
        onChange={(e) => edit({ ...value, ops: { ...value.ops, delivery: e.target.value as AlertOpsDelivery } })}>
        {ALERT_OPS_DELIVERY_CHOICES.map((d) => <option key={d} value={d}>{OPS_DELIVERY_LABELS[d]}</option>)}
      </select>

      <h3 className={styles.platformFeeTitle} style={{ marginTop: 16, fontSize: 15 }}>Staging allowlist</h3>
      <p className={styles.platformFeeHint}>Outside production no alert, digest, snooze, ladder or service call email goes to any address that is not
        listed here. Empty: none goes. {view.effective.production ? 'This is production: the list has no effect here.' : 'This is not production: the list is in force.'}</p>
      <textarea id="alert-staging-list" aria-label="Staging allowlist" className={styles.platformFeeInput} rows={3} style={{ width: '100%', maxWidth: 420 }} disabled={saving}
        value={allowText ?? value.staging_allowlist.join('\n')} onChange={(e) => { setAllowText(e.target.value); setSaved(false); }} />

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
        <button type="button" className={styles.platformFeeBtn} disabled={!dirty || saving} onClick={() => void save()}>
          {saving ? <><Loader2 size={13} className={styles.spin} /> Saving</> : saved && !dirty ? <><Check size={13} /> Saved</> : <>Save</>}
        </button>
        <button type="button" className={styles.platformFeeBtn} disabled={previewing} onClick={() => void runPreview()}>
          {previewing ? <><Loader2 size={13} className={styles.spin} /> Replaying 7 days</> : dirty ? 'Preview these changes' : 'Preview the last 7 days'}
        </button>
      </div>
      {error && <p className={styles.platformFeeHint} role="alert" style={{ color: 'var(--color-err)' }}>{error}</p>}
      {previewError && <p className={styles.platformFeeHint} role="alert" style={{ color: 'var(--color-err)' }}>{previewError}</p>}
      {preview && <PreviewTable p={preview} />}
    </>}
  </section>;
}

function PreviewTable({ p }: { p: AlertPreview }) {
  const rows = previewRows(p);
  const cell = { padding: '4px 8px', textAlign: 'right' as const };
  return <div style={{ marginTop: 16 }}>
    <h3 className={styles.platformFeeTitle} style={{ fontSize: 15 }}>The last 7 days</h3>
    <p className={styles.platformFeeHint}>{new Date(p.window.from).toLocaleString()} to {new Date(p.window.to).toLocaleString()}
      {p.window.partial ? `. Partial: outage records start ${p.window.coverage_from ? new Date(p.window.coverage_from).toLocaleString() : 'later'}.` : '.'}</p>
    <div style={{ overflowX: 'auto' }}><table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
      <thead><tr>{['Email', 'Saved settings', 'These settings', 'Sent (records)'].map((h, i) => <th key={h} style={{ ...cell, textAlign: i ? 'right' : 'left' }}>{h}</th>)}</tr></thead>
      <tbody>
        {rows.map((r) => <tr key={r.type} style={{ borderTop: '1px solid var(--color-border, #dce3df)' }}>
          <td style={{ ...cell, textAlign: 'left' }}>{r.label}</td>
          <td style={cell}>{r.current}</td>
          <td style={cell}>{r.proposed}</td>
          <td style={cell}>{r.approximate ? `about ${r.actual}` : r.actual}</td>
        </tr>)}
        <tr style={{ borderTop: '2px solid var(--color-border, #dce3df)', fontWeight: 600 }}>
          <td style={{ ...cell, textAlign: 'left' }}>Total</td><td style={cell}>{p.current.total}</td><td style={cell}>{p.proposed.total}</td><td style={cell}>{p.actual.total}</td>
        </tr>
      </tbody>
    </table></div>
    <p className={styles.platformFeeHint}>
      With these settings: buildings {p.proposed.by_class.building.emails} emails to {p.proposed.by_class.building.recipients} people (at most {p.proposed.by_class.building.max_per_recipient_day} to one person in a day),
      homes {p.proposed.by_class.home.emails}, EVI ops {p.proposed.by_class.evi_ops.emails}.
    </p>
    {p.top_chargers.length > 0 && <p className={styles.platformFeeHint}>Most emails: {p.top_chargers.slice(0, 5).map((c) => `${c.identity}${c.site ? ` at ${c.site}` : ''} (${c.current} now, ${c.proposed} with these)`).join('; ')}.</p>}
    <ul className={styles.platformFeeHint} style={{ paddingLeft: 18 }}>{p.notes.map((n) => <li key={n}>{n}</li>)}</ul>
  </div>;
}

export default AlertSettingsPage;
